import { Typography } from '@ethings/ui-toolkit';
import { Box } from '@mui/material';
import { ResponsiveStyleValue, SxProps } from '@mui/system';
import { FC, PropsWithChildren, type JSX } from 'react';
import { useResponsiveText } from '../../hooks';
import { innerText } from '../../util';

interface Props extends PropsWithChildren {
  'data-testid'?: string;
  subtitle?: JSX.Element;
  direction?: ResponsiveStyleValue<'row' | 'row-reverse' | 'column' | 'column-reverse'>;
  label: string;
  variant?: 'subtitle1' | 'subtitle2' | 'body1' | 'body2' | 'caption';
  sx?: SxProps;
}

export const LabelIcon: FC<Props> = ({ children, label, subtitle, variant = 'caption', sx, direction = 'row', ...props }) => {
  const textAlignment = direction === 'row' || direction === 'row-reverse' ? 'left' : 'center';

  const labelLength = innerText(label).length;

  const variantSizeMap = {
    subtitle1: '1rem',
    subtitle2: '0.875rem',
    body1: '1rem',
    body2: '0.875rem',
    caption: '0.75rem',
  };

  const baseSize = variantSizeMap[variant];
  const labelStyles = useResponsiveText(baseSize, labelLength, {
    minFontSize: variant === 'caption' ? '0.65rem' : '0.75rem',
    maxFontSize: baseSize,
    textLengthThreshold: 20,
  });

  return <Box sx={{ display: 'flex', flexDirection: direction, textAlign: textAlignment, marginTop: 0, ...sx }} data-testid={props['data-testid']}>
    <Box sx={{ display: 'flex', flexGrow: 1, alignItems: 'center', justifyContent: 'center' }}>{children}</Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <Typography
        m={0}
        variant={variant}
        style={{
          fontSize: labelStyles.fontSize,
          lineHeight: labelStyles.lineHeight,
          wordBreak: labelStyles.wordBreak,
          whiteSpace: labelStyles.whiteSpace,
          overflow: labelStyles.overflow,
          textOverflow: labelStyles.textOverflow,
        }}
      >
        {label}
      </Typography>
      {subtitle && (
        <>{subtitle}</>
      )}
    </Box>
  </Box>;
};
